/* ============================================================
   PST Pool Kit — PDP liner picker + accessory recommender + kit rail
   Ported from "Pool Templates.dc.html" (PDP route).

   Differs from the prototype in one important way: a liner is a
   real Shopify product variant (pattern + thickness + attachment
   resolve to a specific liner product), not a base price + add-ons.
   The whole kit is added with ONE /cart/add.js items[] POST, then
   the theme's real cart drawer is opened.
   ============================================================ */
(function () {
  'use strict';

  function money(cents, currency) {
    var amount = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency', currency: currency || 'USD',
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2
      }).format(amount);
    } catch (e) { return '$' + Math.round(amount).toLocaleString(); }
  }

  class PstPoolKit extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.handle = this.getAttribute('data-pool-handle') || 'pool';
      this.currency = this.getAttribute('data-currency') || 'USD';
      this.pool = {
        variantId: this.getAttribute('data-pool-variant'),
        price: parseInt(this.getAttribute('data-pool-price') || '0', 10),
        title: this.getAttribute('data-pool-title') || 'Pool',
        meta: this.getAttribute('data-pool-meta') || '',
        image: this.getAttribute('data-pool-image') || ''
      };

      this.liners = this.parseLiners();
      this.defaultLinerPattern = this.getAttribute('data-default-liner-pattern') || '';
      this.kit = this.load();
      // On a fresh visit (no saved kit), pre-select a default liner pattern when the
      // section asks for one. The liner is required, so start with a sensible pick;
      // a returning shopper's saved choices (including an intentional removal) win.
      if (this._fresh && !this.kit.liner && this.defaultLinerPattern) {
        this.selectDefaultLiner(this.defaultLinerPattern);
      }

      this.bind();
      this.renderLiner();
      this.renderAccessories();
      this.renderRail();
    }

    /* ---- persistence ---- */
    storeKey() { return 'pstkit:' + this.handle; }
    load() {
      try {
        var v = JSON.parse(sessionStorage.getItem(this.storeKey()));
        if (v && v.acc) { this._fresh = false; return v; }
      } catch (e) {}
      this._fresh = true;
      return { liner: null, acc: {} };
    }
    persist() { try { sessionStorage.setItem(this.storeKey(), JSON.stringify(this.kit)); } catch (e) {} }

    parseLiners() {
      var node = this.querySelector('[data-pst-liner-data]');
      if (!node) return [];
      try { return JSON.parse(node.textContent) || []; } catch (e) { return []; }
    }

    // Pre-select the first liner matching a pattern (used for the default pick).
    // No-ops gracefully if this pool has no liner in that pattern.
    selectDefaultLiner(pattern) {
      var match = null;
      for (var i = 0; i < this.liners.length; i++) {
        if (this.liners[i].pattern === pattern) { match = this.liners[i]; break; }
      }
      if (!match) return;
      var s = { pattern: match.pattern, thickness: match.thickness, attachment: match.attachment };
      this.kit.liner = {
        sel: s, variantId: match.variantId, price: match.price,
        title: match.title, image: match.image,
        meta: [s.thickness, s.attachment].filter(Boolean).join(' · ')
      };
      this.persist();
    }

    /* ---- events ---- */
    bind() {
      var self = this;
      this.addEventListener('click', function (e) {
        var sw = e.target.closest('[data-pst-liner-pattern]');
        if (sw && !sw.disabled) { self.setLiner('pattern', sw.getAttribute('data-pst-liner-pattern')); return; }
        var th = e.target.closest('[data-pst-liner-thick]');
        if (th && !th.disabled) { self.setLiner('thickness', th.getAttribute('data-pst-liner-thick')); return; }
        var at = e.target.closest('[data-pst-liner-attach]');
        if (at && !at.disabled) { self.setLiner('attachment', at.getAttribute('data-pst-liner-attach')); return; }
        if (e.target.closest('[data-pst-liner-remove]')) { self.removeLiner(); return; }

        var skip = e.target.closest('[data-pst-step-skip]');
        if (skip) { self.toggleSkip(skip); return; }

        var add = e.target.closest('[data-pst-acc-toggle]');
        if (add) { self.toggleAcc(add.getAttribute('data-pst-acc-toggle')); return; }
        var inc = e.target.closest('[data-pst-acc-inc]');
        if (inc) { self.incAcc(inc.getAttribute('data-pst-acc-inc'), 1); return; }
        var dec = e.target.closest('[data-pst-acc-dec]');
        if (dec) { self.incAcc(dec.getAttribute('data-pst-acc-dec'), -1); return; }
        var rm = e.target.closest('[data-pst-rail-remove]');
        if (rm) { self.railRemove(rm.getAttribute('data-pst-rail-remove')); return; }

        if (e.target.closest('[data-pst-decide]')) { self.decideForMe(e.target.closest('[data-pst-decide]')); return; }

        if (e.target.closest('[data-pst-addkit]')) { self.addKitToCart(); return; }

        if (e.target.closest('[data-pst-kit-toggle]')) { self.toggleKitSheet(); return; }
        if (e.target.closest('[data-pst-kit-close]')) { self.toggleKitSheet(false); return; }

        var thumb = e.target.closest('[data-pst-gallery-thumb]');
        if (thumb) { self.swapGallery(thumb); return; }
      });
    }

    swapGallery(thumb) {
      var src = thumb.getAttribute('data-full');
      var main = this.querySelector('[data-pst-gallery-main] img');
      if (src && main) { main.src = src; main.srcset = ''; }
      this.querySelectorAll('[data-pst-gallery-thumb]').forEach(function (t) {
        t.setAttribute('aria-pressed', String(t === thumb));
      });
    }

    /* ---- liner selection (pattern/thickness/attachment → product) ---- */
    sel() { return this.kit.liner ? this.kit.liner.sel : null; }

    available(dim, value, partial) {
      // is there a liner product matching the partial selection + this dim=value?
      var p = Object.assign({}, partial); p[dim] = value;
      return this.liners.some(function (l) {
        return (!p.pattern || l.pattern === p.pattern)
          && (!p.thickness || l.thickness === p.thickness)
          && (!p.attachment || l.attachment === p.attachment);
      });
    }

    resolveLiner(s) {
      return this.liners.find(function (l) {
        return l.pattern === s.pattern && l.thickness === s.thickness && l.attachment === s.attachment;
      }) || null;
    }

    setLiner(dim, value) {
      var cur = this.sel() || { pattern: null, thickness: null, attachment: null };
      // Always honor the dimension the user just clicked. Prefer a liner that also
      // keeps their other current choices; otherwise fall back to the best match for
      // the clicked value. (Clicking a new pattern must always swap the liner.)
      var want = Object.assign({}, cur);
      want[dim] = value;
      var product = this.resolveLiner(want);
      if (!product) {
        var others = ['pattern', 'thickness', 'attachment'].filter(function (d) { return d !== dim; });
        var best = null, bestScore = -1;
        this.liners.forEach(function (l) {
          if (l[dim] !== value) return;
          var score = 0;
          others.forEach(function (d) { if (cur[d] && l[d] === cur[d]) score++; });
          if (score > bestScore) { bestScore = score; best = l; }
        });
        product = best;
      }
      if (product) {
        var s = { pattern: product.pattern, thickness: product.thickness, attachment: product.attachment };
        this.kit.liner = {
          sel: s, variantId: product.variantId, price: product.price,
          title: product.title, image: product.image,
          meta: [s.thickness, s.attachment].filter(Boolean).join(' · ')
        };
      }
      this.persist(); this.renderLiner(); this.renderRail();
    }
    removeLiner() { this.kit.liner = null; this.persist(); this.renderLiner(); this.renderRail(); }

    renderLiner() {
      var self = this, s = this.sel();
      this.querySelectorAll('[data-pst-liner-pattern]').forEach(function (b) {
        var v = b.getAttribute('data-pst-liner-pattern');
        b.setAttribute('aria-pressed', String(!!s && s.pattern === v));
      });
      this.querySelectorAll('[data-pst-liner-thick]').forEach(function (b) {
        var v = b.getAttribute('data-pst-liner-thick');
        b.setAttribute('aria-pressed', String(!!s && s.thickness === v));
        b.disabled = s ? !self.available('thickness', v, { pattern: s.pattern }) : false;
      });
      this.querySelectorAll('[data-pst-liner-attach]').forEach(function (b) {
        var v = b.getAttribute('data-pst-liner-attach');
        b.setAttribute('aria-pressed', String(!!s && s.attachment === v));
        b.disabled = s ? !self.available('attachment', v, { pattern: s.pattern, thickness: s.thickness }) : false;
      });
      var box = this.querySelector('[data-pst-liner-selected]');
      if (box) {
        box.hidden = !this.kit.liner;
        if (this.kit.liner) {
          this.setText('[data-pst-liner-sel-title]', this.kit.liner.title);
          this.setText('[data-pst-liner-sel-price]', money(this.kit.liner.price, this.currency));
        }
      }
      var priceHint = this.querySelector('[data-pst-liner-headprice]');
      if (priceHint) {
        priceHint.textContent = this.kit.liner
          ? money(this.kit.liner.price, this.currency)
          : (this.liners.length ? 'from ' + money(Math.min.apply(null, this.liners.map(function (l) { return l.price; })), this.currency) : '');
      }
    }

    /* ---- accessories ---- */
    accEls() { return Array.prototype.slice.call(this.querySelectorAll('[data-pst-acc-card]')); }

    toggleAcc(id) {
      if (this.kit.acc[id]) { delete this.kit.acc[id]; }
      else { this.kit.acc[id] = 1; this.clearSkipFor(id); }
      this.persist(); this.renderAccessories(); this.renderRail();
    }

    /* ---- "Decide for me": fill every required step with the first option the
       merchant listed, without disturbing anything already chosen ---- */
    decideForMe(btn) {
      var self = this;
      var added = 0;

      if (!this.kit.liner) {
        var sw = this.querySelector('[data-pst-liner-pattern]:not([disabled])');
        if (sw) { this.setLiner('pattern', sw.getAttribute('data-pst-liner-pattern')); added++; }
      }

      Array.prototype.slice.call(this.querySelectorAll('[data-pst-step-panel]')).forEach(function (panel) {
        var opts = Array.prototype.slice.call(panel.querySelectorAll('[data-pst-acc-toggle]'));
        if (!opts.length) return;
        var chosen = opts.some(function (o) { return self.kit.acc[o.getAttribute('data-pst-acc-toggle')]; });
        if (chosen) return;
        if (panel.getAttribute('data-skipped') === 'true') return;
        self.toggleAcc(opts[0].getAttribute('data-pst-acc-toggle'));
        added++;
      });

      if (btn) {
        var label = btn.querySelector('[data-pst-decide-label]') || btn;
        var original = label.textContent;
        label.textContent = added ? 'Added to your kit' : 'Your kit is already complete';
        btn.disabled = true;
        setTimeout(function () { label.textContent = original; btn.disabled = false; }, 2600);
      }
      var rail = document.querySelector('.pst-kit-rail');
      if (rail && added) { rail.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }
    }

    /* ---- per-step "I already have one / skip" toggle (visual only) ---- */
    toggleSkip(btn) {
      var section = btn.closest('.pst-acc-group');
      if (!section) return;
      var now = section.getAttribute('data-skipped') === 'true';
      section.setAttribute('data-skipped', String(!now));
      btn.setAttribute('aria-pressed', String(!now));
    }
    clearSkipFor(id) {
      var card = this.querySelector('[data-pst-acc-card][data-id="' + CSS.escape(id) + '"]');
      var section = card ? card.closest('.pst-acc-group') : null;
      if (!section) return;
      section.setAttribute('data-skipped', 'false');
      var btn = section.querySelector('[data-pst-step-skip]');
      if (btn) btn.setAttribute('aria-pressed', 'false');
    }
    incAcc(id, delta) {
      var q = (this.kit.acc[id] || 0) + delta;
      if (q <= 0) delete this.kit.acc[id]; else this.kit.acc[id] = q;
      this.persist(); this.renderAccessories(); this.renderRail();
    }

    renderAccessories() {
      var self = this;
      // Cards are grouped into stacked category panels server-side (no tabs);
      // here we just sync each card's in-kit / stepper / quantity state.
      this.accEls().forEach(function (card) {
        var id = card.getAttribute('data-id');
        var inKit = self.kit.acc[id] > 0;
        card.setAttribute('data-in-kit', String(inKit));
        var addBtn = card.querySelector('[data-pst-acc-add-wrap]');
        var stepWrap = card.querySelector('[data-pst-acc-step-wrap]');
        if (addBtn) addBtn.hidden = inKit;
        if (stepWrap) stepWrap.hidden = !inKit;
        var qtyEl = card.querySelector('[data-pst-acc-qty]');
        if (qtyEl) qtyEl.textContent = String(self.kit.acc[id] || 0);
      });
    }

    /* ---- kit rail ---- */
    subtotalCents() {
      var sum = this.pool.price;
      if (this.kit.liner) sum += this.kit.liner.price;
      var self = this;
      Object.keys(this.kit.acc).forEach(function (id) {
        var card = self.querySelector('[data-pst-acc-card][data-id="' + CSS.escape(id) + '"]');
        if (card) sum += parseInt(card.getAttribute('data-price') || '0', 10) * self.kit.acc[id];
      });
      return sum;
    }

    accData(id) {
      var card = this.querySelector('[data-pst-acc-card][data-id="' + CSS.escape(id) + '"]');
      if (!card) return null;
      return {
        variantId: card.getAttribute('data-variant'),
        price: parseInt(card.getAttribute('data-price') || '0', 10),
        title: card.getAttribute('data-title'),
        image: card.getAttribute('data-image') || ''
      };
    }

    renderRail() {
      var self = this;
      // liner row
      var linerRow = this.querySelector('[data-pst-rail-liner]');
      if (linerRow) {
        linerRow.hidden = !this.kit.liner;
        if (this.kit.liner) {
          this.setText('[data-pst-rail-liner-title]', this.kit.liner.title);
          this.setText('[data-pst-rail-liner-meta]', this.kit.liner.meta);
          this.setText('[data-pst-rail-liner-price]', money(this.kit.liner.price, this.currency));
          var linerThumb = linerRow.querySelector('[data-pst-rail-liner-thumb]');
          if (linerThumb) {
            if (this.kit.liner.image) {
              linerThumb.innerHTML = '<img src="' + this.kit.liner.image + '" alt="" loading="lazy">';
              linerThumb.removeAttribute('style');
            } else {
              linerThumb.innerHTML = self.wavesIcon();
              linerThumb.style.background = 'var(--splash-200)';
              linerThumb.style.color = 'var(--splash-700)';
            }
          }
        }
      }
      // accessory rows
      var accWrap = this.querySelector('[data-pst-rail-acc]');
      if (accWrap) {
        var html = '';
        Object.keys(this.kit.acc).forEach(function (id) {
          var a = self.accData(id); if (!a) return;
          var qty = self.kit.acc[id];
          html += '<div class="pst-kit__row pst-kit__row--bordered">' +
            '<span class="pst-kit__thumb">' + (a.image ? '<img src="' + a.image + '" alt="" loading="lazy">' : self.boxIcon()) + '</span>' +
            '<div class="pst-kit__row-body"><div class="pst-kit__row-title">' + a.title + '</div>' +
            (qty > 1 ? '<div class="pst-kit__row-meta">Qty ' + qty + '</div>' : '') +
            '<button type="button" class="pst-kit__row-remove" data-pst-rail-remove="' + id + '">Remove</button></div>' +
            '<div class="pst-kit__row-price">' + money(a.price * qty, self.currency) + '</div></div>';
        });
        accWrap.innerHTML = html;
      }
      // empty hint
      var hint = this.querySelector('[data-pst-rail-hint]');
      var extras = (this.kit.liner ? 1 : 0) + Object.keys(this.kit.acc).length;
      if (hint) hint.hidden = extras > 0;
      // subtotal + count (mirrored into the desktop rail AND the mobile bar)
      this.setAll('[data-pst-rail-subtotal]', money(this.subtotalCents(), this.currency));
      var count = 1 + (this.kit.liner ? 1 : 0) +
        Object.keys(this.kit.acc).reduce(function (n, id) { return n + self.kit.acc[id]; }, 0);
      this.setAll('[data-pst-rail-count]', count + (count === 1 ? ' item' : ' items'));
    }

    railRemove(id) {
      if (id === 'liner') this.removeLiner();
      else { delete this.kit.acc[id]; this.persist(); this.renderAccessories(); this.renderRail(); }
    }

    /* ---- mobile "Your kit" bottom sheet: expand/collapse (CSS: .pst-kit-rail.is-open) ---- */
    toggleKitSheet(force) {
      var rail = this.querySelector('.pst-kit-rail');
      if (!rail) return;
      var open = typeof force === 'boolean' ? force : !rail.classList.contains('is-open');
      rail.classList.toggle('is-open', open);
      var btn = this.querySelector('[data-pst-kit-toggle]');
      if (btn) btn.setAttribute('aria-expanded', String(open));
    }

    /* ---- add complete kit to cart (single multi-item POST) ---- */
    addKitToCart() {
      var self = this;
      var items = [{ id: this.pool.variantId, quantity: 1 }];
      if (this.kit.liner && this.kit.liner.variantId) items.push({ id: this.kit.liner.variantId, quantity: 1 });
      Object.keys(this.kit.acc).forEach(function (id) {
        var a = self.accData(id);
        if (a && a.variantId) items.push({ id: a.variantId, quantity: self.kit.acc[id] });
      });
      if (!items.length || !items[0].id) return;

      var cta = this.querySelector('[data-pst-addkit]');
      if (cta) cta.setAttribute('aria-busy', 'true');

      var drawer = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (drawer && typeof drawer.setActiveElement === 'function' && cta) { drawer.setActiveElement(cta); }
      var payload = { items: items };
      var sections = this.cartSections(drawer);
      if (sections.length) { payload.sections = sections.join(','); payload.sections_url = window.location.pathname; }

      fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload)
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res.status) { self.cartError(res); return; }
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'pst-pool-kit', productVariantId: items[0].id });
          }
          if (drawer && typeof drawer.renderContents === 'function') {
            drawer.renderContents(res);
          } else {
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          }
        })
        .catch(function () { window.location.href = (window.routes && window.routes.cart_url) || '/cart'; })
        .finally(function () {
          if (cta) cta.removeAttribute('aria-busy');
          // Dawn's renderContents only clears `is-empty` from `.drawer__inner`; the
          // <cart-drawer> element keeps the class it rendered with, so after a FIRST add
          // (cart was empty) the item list stays hidden until reload. The theme's own
          // product-form strips it here in its finally — mirror that exactly.
          if (drawer && drawer.classList.contains('is-empty')) { drawer.classList.remove('is-empty'); }
        });
    }

    cartSections(drawer) {
      var ids = [];
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        try { drawer.getSectionsToRender().forEach(function (s) { if (s.id) ids.push(s.id); }); } catch (e) {}
      }
      if (!ids.length) { ids = ['cart-drawer', 'cart-icon-bubble']; }
      return ids;
    }

    cartError(res) {
      var msg = (res && res.description) || (window.cartStrings && window.cartStrings.error) || 'Could not add to cart.';
      var box = this.querySelector('[data-pst-kit-error]');
      if (box) { box.textContent = msg; box.hidden = false; }
      else { window.alert(msg); }
    }

    /* ---- helpers ---- */
    setText(sel, text) { var el = this.querySelector(sel); if (el) el.textContent = text; }
    setAll(sel, text) { this.querySelectorAll(sel).forEach(function (el) { el.textContent = text; }); }
    boxIcon() { return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8 12 3 3 8v8l9 5 9-5V8Z"/><path d="m3 8 9 5 9-5M12 13v8"/></svg>'; }
    wavesIcon() { return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/><path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/></svg>'; }
  }
  if (!customElements.get('pst-pool-kit')) customElements.define('pst-pool-kit', PstPoolKit);
})();
